// Clinical Service - Conditions, Medications, Labs, Vitals, Visits, Documents
import api from './api';

export const clinicalService = {
  // ==================== CONDITIONS ====================
  async getConditions(patientId) {
    try {
      const response = await api.get(`/api/v1/clinical/conditions/${patientId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch conditions' };
    }
  },

  async createCondition(conditionData) {
    try {
      const response = await api.post('/api/v1/clinical/conditions', conditionData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to create condition' };
    }
  },

  // ==================== MEDICATIONS ====================
  async getMedications(patientId, status = '') {
    try {
      const params = status ? `?status_filter=${status}` : '';
      const response = await api.get(`/api/v1/clinical/medications/${patientId}${params}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch medications' };
    }
  },

  async createMedication(medicationData) {
    try {
      const response = await api.post('/api/v1/clinical/medications', medicationData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to create medication' };
    }
  },

  // ==================== LAB RESULTS ====================
  async getLabResults(patientId, testName = '') {
    try {
      const params = testName ? `?test_name=${testName}` : '';
      const response = await api.get(`/api/v1/clinical/lab-results/${patientId}${params}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch lab results' };
    }
  },

  async createLabResult(labData) {
    try {
      const response = await api.post('/api/v1/clinical/lab-results', labData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to create lab result' };
    }
  },

  // ==================== VITALS ====================
  async getVitals(patientId, limit = 10) {
    try {
      const response = await api.get(`/api/v1/clinical/vitals/${patientId}?limit=${limit}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch vitals' };
    }
  },

  async createVitals(vitalsData) {
    try {
      const response = await api.post('/api/v1/clinical/vitals', vitalsData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to record vitals' };
    }
  },

  // ==================== VISITS ====================
  async getVisits(patientId, limit = 20) {
    try {
      const response = await api.get(`/api/v1/clinical/visits/${patientId}?limit=${limit}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch visits' };
    }
  },

  async createVisit(visitData) {
    try {
      const response = await api.post('/api/v1/clinical/visits', visitData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to create visit' };
    }
  },

  // ==================== TIMELINE ====================
  async getTimeline(patientId, limit = 50) {
    try {
      const response = await api.get(`/api/v1/clinical/timeline/${patientId}?limit=${limit}`);
      return { success: true, data: response.data.timeline };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch timeline' };
    }
  },

  // ==================== DOCUMENTS ====================
  async getDocuments(patientId, docType = '') {
    try {
      const params = docType ? `?doc_type=${docType}` : '';
      const response = await api.get(`/api/v1/clinical/documents/${patientId}${params}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch documents' };
    }
  },

  async createDocument(documentData) {
    try {
      const response = await api.post('/api/v1/clinical/documents', documentData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to create document' };
    }
  },

  // ==================== COMBINED PATIENT DATA ====================
  async getPatientFullData(patientId) {
    try {
      const [conditions, medications, labResults, vitals, visits, documents, timeline] = await Promise.all([
        this.getConditions(patientId),
        this.getMedications(patientId),
        this.getLabResults(patientId),
        this.getVitals(patientId, 20),
        this.getVisits(patientId),
        this.getDocuments(patientId),
        this.getTimeline(patientId),
      ]);

      return {
        success: true,
        data: {
          conditions: conditions.data || [],
          medications: medications.data || [],
          labResults: labResults.data || [],
          vitals: vitals.data || [],
          visits: visits.data || [],
          documents: documents.data || [],
          timeline: timeline.data || [],
        },
      };
    } catch (error) {
      return { success: false, error: 'Failed to fetch patient data' };
    }
  },
};

export default clinicalService;
